/**
 * Dimension-based classification of RoomPlan `storage` objects.
 *
 * RoomPlan tags a wide range of casegoods (wardrobes, bookshelves, dressers,
 * sideboards, nightstands, …) as a single `storage` category. We keep the
 * stored object `type` as `storage` and decide which procedural sub-model to
 * render at draw time, purely from the object's footprint dimensions — with an
 * optional persisted human override that always wins.
 *
 * This module is PURE: no Three.js, no I/O. It is unit-tested in
 * `classifyStorage.test.js`.
 */

/**
 * The valid storage sub-types, in priority order of the dimension rules.
 * `dresser` is the catch-all and is listed last.
 */
export const STORAGE_SUB_TYPES = [
  'wall-shelf',
  'nightstand',
  'wardrobe',
  'bookshelf',
  'console',
  'dresser',
];

const DEFAULT_SUB_TYPE = 'dresser';

/**
 * Dimension thresholds (meters). Tuned to the approved starting rules; kept in
 * one place so the priority order below and the tests reference the same
 * numbers. Boundaries are inclusive (`>=` / `<=`) on the named side.
 */
const THRESHOLDS = {
  // nightstand: small footprint AND low. A bedside cabinet is narrow and short;
  // these two together exclude every larger casegood.
  nightstandMaxWidth: 0.6, // ≤ 0.6 m (~24") wide
  nightstandMaxHeight: 0.8, // ≤ 0.8 m (~31") tall

  // wardrobe: tall AND deep. Full-height clothes storage you hang things in.
  wardrobeMinHeight: 1.5, // ≥ 1.5 m (~59") tall
  wardrobeMinDepth: 0.45, // ≥ 0.45 m (~18") deep

  // bookshelf: tall AND shallow. Open shelving stands tall but stays thin.
  bookshelfMinHeight: 1.2, // ≥ 1.2 m (~47") tall
  bookshelfMaxDepth: 0.4, // ≤ 0.4 m (~16") deep

  // console: low AND wide. A sideboard / low media unit — long and short.
  consoleMaxHeight: 0.7, // ≤ 0.7 m (~28") tall
  consoleMinWidth: 1.0, // ≥ 1.0 m (~39") wide
};

/**
 * Classify a `storage` object into a procedural sub-type.
 *
 * Priority order (first match wins) — chosen so each rule's "definition" excludes
 * the cases the later rules want, keeping the function total and deterministic:
 *   1. explicit override (human pick) — always wins
 *   1b. wall-shelf — mounted rather than standing (stamped upstream; legs
 *       would otherwise dangle). Footprint cannot detect this.
 *   2. nightstand — small + low (smallest pieces; checked first so a short,
 *      narrow unit never falls through to console/dresser)
 *   3. wardrobe   — tall + deep
 *   4. bookshelf  — tall + shallow (after wardrobe so a tall+deep unit is a
 *      wardrobe, not a bookshelf)
 *   5. console    — low + wide
 *   6. dresser    — catch-all (every mid-size chest)
 *
 * Missing/non-finite dimensions degrade to 0, so a malformed object still
 * resolves deterministically (it lands on `dresser` unless the 0s happen to
 * satisfy a small/low rule — see tests).
 *
 * @param {{width?:number, height?:number, depth?:number}} dimensions meters
 * @param {string} [explicitSubType] persisted human override
 * @returns {'wardrobe'|'bookshelf'|'dresser'|'console'|'nightstand'}
 */
export function classifyStorage(dimensions, explicitSubType, options) {
  // 1. Human override always wins (only when it's a recognized sub-type).
  if (STORAGE_SUB_TYPES.includes(explicitSubType)) {
    return explicitSubType;
  }

  // 2. Mounted on a wall? Every dimension-based sub-model below stands on
  //    four metal legs, which dangle in mid-air on a unit that is fixed to
  //    the wall (structure 31B79781's 0F1BF677: 47.6" tall, floating 38" up).
  //    Footprint cannot tell us this — the mount is stamped by
  //    post-processing, which is the only place that knows the room's floor.
  if (options?.mount === 'wall') {
    return 'wall-shelf';
  }

  const width = toFinite(dimensions?.width);
  const height = toFinite(dimensions?.height);
  const depth = toFinite(dimensions?.depth);

  // 2. nightstand — small footprint AND low.
  if (
    width <= THRESHOLDS.nightstandMaxWidth &&
    height <= THRESHOLDS.nightstandMaxHeight
  ) {
    return 'nightstand';
  }

  // 3. wardrobe — tall AND deep.
  if (
    height >= THRESHOLDS.wardrobeMinHeight &&
    depth >= THRESHOLDS.wardrobeMinDepth
  ) {
    return 'wardrobe';
  }

  // 4. bookshelf — tall AND shallow.
  if (
    height >= THRESHOLDS.bookshelfMinHeight &&
    depth <= THRESHOLDS.bookshelfMaxDepth
  ) {
    return 'bookshelf';
  }

  // 5. console — low AND wide.
  if (
    height <= THRESHOLDS.consoleMaxHeight &&
    width >= THRESHOLDS.consoleMinWidth
  ) {
    return 'console';
  }

  // 6. dresser — mid catch-all.
  return DEFAULT_SUB_TYPE;
}

/** Coerce to a finite number, treating anything else as 0. */
function toFinite(value) {
  return Number.isFinite(value) ? value : 0;
}

export { THRESHOLDS as STORAGE_THRESHOLDS };
